import { searchItems } from '@sbt-web/network/client';
import { AdsBeforeListingBlock } from '@client/components/Adv/AdSense/listing/AdsBeforeListingBlock';
import type { RootState } from '@reducers/index';
import { useViewport } from '@sbt-web/hooks';
import { AdTypes, CategoryID, SearchResult } from '@sbt-web/network/types';
import { Category, isShippableCategory } from '@sbt-web/category-utils';
import { GeoEntry, SeoMetaPayload, TuttaItalia } from '@sbt-web/networking';
import { ItemSkeleton } from '@sbt-web/ui';
import { CSSTransitionWithRef } from '@components/CSSTransitionWithRef';
import { type DecoratedItem } from '@shared/models';
import { sendNotification } from '@tools/Notifications';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ListingHeading } from '../../SEO/ListingHeading/ListingHeading';
import { ListingItem } from './ListingItem';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { mapSearchStateToSearchParams } from '@reducers/items';
import { SearchState } from '@reducers/search';
import ZrpExtendedSearch from '../Elements/ZrpExtendedSearch';
import type { FiltersConfigState } from '@reducers/filtersConfig';

import ownClasses from './ItemListContainer.module.scss';

const ZeroResults = dynamic(() =>
  import('../Elements/ZeroResults').then((m) => m.ZeroResults)
);

const NationwideSearchExpander = dynamic(
  () => import('../../Search/Elements/SearchNationwide'),
  { ssr: false }
);

interface StateProps {
  items: DecoratedItem[];
  loading: boolean;
  hasError: boolean;
  listingCategory: Category;
  adType: AdTypes;
  query: string;
  geo: GeoEntry;
  filters: Record<string, string>;
  filtersLabels?: Record<string, string>;
  isModalOpened: boolean;
  search: SearchState;
  filtersConfig: FiltersConfigState;
}

interface OwnProps {
  seo?: SeoMetaPayload;
  macroCategory: string;
  handleItemClick: () => void;
  enableOverrideUrgentForPro?: boolean;
}

type Props = OwnProps & StateProps;

const itemSkeletonsCount = 10;
const ZRP_EXPANDED_SEARCH_ITEMS_COUNT = 35;

function extendSearch(search: SearchState): SearchState {
  return {
    ...search,
    geo: {
      region: TuttaItalia,
    },
    includeShippableOnly: true,
    page: 1,
  };
}

function buildExtSearchParams(
  search: SearchState,
  filtersConfig: FiltersConfigState
) {
  const params = mapSearchStateToSearchParams(search, filtersConfig);
  params.lim = ZRP_EXPANDED_SEARCH_ITEMS_COUNT;
  return params;
}

const HIDE_PRO_DETAILS_TEST_KEY = 'subito-web-pro-auto-details';

export function ItemListContainer({
  filters,
  filtersLabels,
  geo,
  hasError,
  items,
  listingCategory,
  adType,
  loading,
  query,
  seo,
  isModalOpened,
  macroCategory,
  handleItemClick,
  search,
  enableOverrideUrgentForPro,
  filtersConfig,
}: Props) {
  const { isMobile, isTablet, isDesktop } = useViewport();

  const { optimizely } = useContext(OptimizelySubitoContext);
  const [jobPhotoVariant, setJobPhotoVariant] = useState<string | null>(null);

  useEffect(() => {
    if (
      listingCategory.id === CategoryID.OfferteLavoro &&
      jobPhotoVariant === null
    ) {
      optimizely?.onReady().then(() => {
        setJobPhotoVariant(optimizely.activate('job-photo-hidden'));
      });
    }
  }, [jobPhotoVariant, listingCategory.id, optimizely]);

  useEffect(() => {
    // Ignore the updates to the listing when the modal is opened, since in
    // mobile we update underneath the filter UI.
    // Ignore updates when loading, since we don't want to track the the intermediate states.
    // Don't track before the variant is set, to prevent doubled-up first views.
    // N.B. this unfortunately triggers multiple times when changing back and
    // forth from the category because the state updates are sequential and
    // not batched.
    if (
      !isModalOpened &&
      !loading &&
      jobPhotoVariant !== null &&
      listingCategory.id === CategoryID.OfferteLavoro
    ) {
      // Linked to the experiment "job-photo-hidden"
      optimizely?.onReady().then(() => optimizely.track('view-job-listing'));
    }
    // Rerun this effect when the items are updated, since it's a new listing
  }, [isModalOpened, loading, jobPhotoVariant, listingCategory.id, optimizely]);

  useEffect(() => {
    if (hasError) {
      sendNotification('Si è verificato un problema', 'alert');
    }
  }, [hasError]);

  useEffect(() => {
    const trackPhoneLead = () => {
      optimizely?.track('show-job-phone');
    };

    window.addEventListener('subito:ShowPhoneContact', trackPhoneLead, {
      passive: true,
    });
    window.addEventListener('subito:PhoneCall', trackPhoneLead, {
      passive: true,
    });

    return () => {
      window.removeEventListener('subito:ShowPhoneContact', trackPhoneLead);
      window.removeEventListener('subito:PhoneCall', trackPhoneLead);
    };
  }, [optimizely]);

  const [extSearchData, setExtSearchData] = useState<SearchResult>();
  const [extendedSearch, setExtendedSearch] = useState<SearchState>();

  const showZRP = items.length === 0;
  const hasExtendedSearch = !!extendedSearch && !!extSearchData?.ads.length;

  useEffect(() => {
    if (!showZRP) return;
    if (!isShippableCategory(listingCategory.id)) return;

    let isMounted = true;

    const applyExtendedSearch = async () => {
      try {
        const extendedSearch = extendSearch(search);
        const params = buildExtSearchParams(extendedSearch, filtersConfig);
        const { body: data } = await searchItems(
          process.env.NEXT_PUBLIC_HADES_BASE_URL,
          params,
          undefined,
          true
        );
        if (!isMounted) return;
        setExtSearchData(data);
        setExtendedSearch(extendedSearch);
      } catch {
        if (!isMounted) return;
        setExtSearchData(undefined);
        setExtendedSearch(undefined);
      }
    };

    applyExtendedSearch();

    return () => {
      isMounted = false;
    };
  }, [showZRP, listingCategory.id, search, filtersConfig]);

  const [hideProDetailsVariant, setHideProDetailsVariant] = useState('A');

  const shouldHideProDetails = hideProDetailsVariant === 'B';

  useEffect(() => {
    if (listingCategory.id === CategoryID.Auto) {
      optimizely?.onReady().then(() => {
        setHideProDetailsVariant(
          optimizely.activate(HIDE_PRO_DETAILS_TEST_KEY) ?? 'A'
        );
      });
    }
  }, [listingCategory.id, optimizely]);

  return (
    <div className={classnames(ownClasses.container)}>
      <div data-google-interstitial="false">
        {!showZRP && <ListingHeading seo={seo} />}
        {showZRP ? <AdsBeforeListingBlock /> : null}
        {isDesktop || isTablet ? (
          <div
            className={classnames(
              'search-expansion-container',
              ownClasses['search-expansion-container'],
              {
                [ownClasses['search-expansion-container--zero-results']]:
                  items.length === 0,
              }
            )}
          >
            <NationwideSearchExpander id="desktop-listing-nationwide-search-expander" />
          </div>
        ) : null}
      </div>

      <CSSTransitionWithRef
        in={loading}
        timeout={300}
        classNames="content"
        mountOnEnter
      >
        <div className={classnames('skeletons', ownClasses.skeletons)}>
          {Array(itemSkeletonsCount)
            .fill(undefined)
            .map((_: unknown, i: number) => (
              <div className="items__item" key={i}>
                <ItemSkeleton flavor={isMobile ? 'small' : 'large'} />
              </div>
            ))}
        </div>
      </CSSTransitionWithRef>
      <div
        className={classnames({
          [ownClasses.placeholder]: loading,
        })}
      >
        <CSSTransitionWithRef
          in={!loading}
          timeout={300}
          classNames="content"
          mountOnEnter
          unmountOnExit
        >
          <div
            className={classnames({
              // Linked to the experiment "job-photo-hidden"
              [ownClasses['updated-job-offer-card']]:
                listingCategory.id === CategoryID.OfferteLavoro &&
                jobPhotoVariant === 'B',
            })}
          >
            {items.map((item, index) => (
              <ListingItem
                key={item.item.urn}
                item={item}
                index={index}
                listingCategory={listingCategory.id}
                adType={adType}
                filters={filters}
                filtersLabels={filtersLabels}
                query={query}
                geo={geo}
                macroCategory={macroCategory}
                handleItemClick={handleItemClick}
                enableOverrideUrgentForPro={enableOverrideUrgentForPro ?? false}
                filterStore={filtersConfig.filtersStore}
                shouldHideProDetails={shouldHideProDetails}
              />
            ))}
          </div>
        </CSSTransitionWithRef>
      </div>

      <div data-google-interstitial="false">
        {showZRP && (
          <>
            {hasExtendedSearch ? (
              <ZrpExtendedSearch
                extendedSearch={extendedSearch}
                extendedSearchData={extSearchData}
                listingCategoryId={listingCategory.id}
                enableOverrideUrgentForPro={enableOverrideUrgentForPro ?? false}
              />
            ) : (
              <ZeroResults listingCategoryLabel={listingCategory.friendly} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default connect<StateProps, void, OwnProps, RootState>((state) => ({
  items: state.items.list,
  isModalOpened:
    state.search.filtersDialogOpen || state.search.radiusDialogOpen,
  loading: state.items.loading,
  hasError: state.items.hasError,
  listingCategory: state.search.category,
  adType: state.search.adType,
  filters: state.search.filters,
  filtersLabels: state.search.filtersLabels,
  query: state.search.query ?? '',
  geo: state.search.geo,
  search: state.search,
  filtersConfig: state.filtersConfig,
}))(ItemListContainer);
